import CommonConstants from '@/constants/common-constants';
import metaInfoConstants from '@/constants/meta-info-constants';
import {
  GET_RUN_BY_ID_WITH_CHECKLIST_VERSION_QUERY,
  GET_RUN_CHECKLIST_INSTRUCTION_BY_RUN_ID_QUERY,
  INSERT_RUN_CHECKLIST_MUTATION
} from '@/graphql/runs.gql';
import { useRunChecklistStore } from '@/stores/run/run-checklist.store';
import {
  Run,
  RunChecklist,
  RunsTaskAssignment,
  WorkflowAssignment
} from '@/types/run.type';
import { useMutation, useQuery } from '@apollo/client';
import { useUserId } from '@nhost/nextjs';
import axios, { AxiosError, CancelToken } from 'axios';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { shallow } from 'zustand/shallow';
import MetaInfo from '../meta-info/meta-info.component';
import RunBuilderComponent from './run-builder.component';

export type RunChecklistTree = {
  checklist: RunChecklist;
  children: RunChecklistTree[];
  parent?: RunChecklistTree;
  isFirstNode?: boolean;
  isLastNode?: boolean;
  lastChildNode?: RunChecklistTree;
};

interface RunChecklistByTaskId {
  [key: string]: RunChecklist;
}

export default function RunByIdComponent() {
  // Variables
  const router = useRouter();
  const { runId, preview } = router.query;
  const userId = useUserId();

  // State
  const [run, setRun] = useState<Run>();
  const [wfMap, setWfMap] = useState<Record<string, WorkflowAssignment[]>>({});
  const [statusMap, setStatusMap] = useState<
    Record<string, RunsTaskAssignment[]>
  >({});
  // State for controlling the sticky message visibility
  const [showAssignorMessage, setShowAssignorMessage] = useState(false);

  // Store
  const [reset] = useRunChecklistStore(s => [s.reset], shallow);

  // GrpahQL
  const { data, loading, error } = useQuery<{ runs: Run[] }>(
    GET_RUN_BY_ID_WITH_CHECKLIST_VERSION_QUERY,
    {
      variables: { id: runId },
      fetchPolicy: 'network-only'
    }
  );

  useEffect(() => {
    if (data?.runs?.[0]) {
      // Reset the store
      reset();

      const run = data.runs[0];
      const wfAssignments =
        run?.workflow_configuration?.workflow_assignments || [];
      const wfMap: Record<string, WorkflowAssignment[]> = {};
      wfAssignments.forEach(a => {
        if (!wfMap[a.task_id]) wfMap[a.task_id] = [];
        wfMap[a.task_id].push(a);
      });
      const statusAssignments = run.runs_task_assignments || [];
      const statusMap: Record<string, RunsTaskAssignment[]> = {};
      statusAssignments.forEach(a => {
        if (!statusMap[a.task_id]) statusMap[a.task_id] = [];
        statusMap[a.task_id].push(a);
      });
      setRun(run);
      setWfMap(wfMap); // ← new state setter
      setStatusMap(statusMap);

      // Check if the user is assigned to any run
      const isUserAssigned = data.runs[0]?.runs_assignments?.some(
        assignment => assignment?.userByUserId?.id === userId
      );

      // Check if the user is assignor to any run
      const isUserAssignor = data.runs[0]?.runs_assignments?.some(
        assignment => assignment?.user?.id === userId
      );

      // Not Assigned User
      if (!isUserAssigned) {
        router.push('/assigned');
      }

      if (isUserAssignor && !isUserAssigned) {
        setShowAssignorMessage(true);
        router.push('/assigned-to-others');
      }

      if (!data?.runs[0]?.checklist_version_id) {
        router.push(`/assigned`);
      }
    }
  }, [data, router]);

  useEffect(() => {
    if (!loading && !data?.runs?.[0]) {
      router.push('/assigned');
    }
  }, [loading, data, router]);

  // Loading
  if (loading || !run) {
    return (
      <div className="px-4 md:px-10 pt-6 pb-6">
        <div className="bg-[#EDE4E0] h-7 rounded-lg w-52 max-w-full mb-6"></div>

        <ul className="grid grid-cols-1 gap-3 mt-8">
          <li className="bg-[#EDE4E0] rounded-lg h-12"></li>
          <li className="bg-[#EDE4E0] rounded-lg h-12"></li>
          <li className="bg-[#EDE4E0] rounded-lg h-12"></li>
          <li className="bg-[#EDE4E0] rounded-lg h-12"></li>
        </ul>
      </div>
    );
  }

  // Not found
  if (
    !data ||
    !data?.runs ||
    data?.runs?.length == 0 ||
    !data.runs[0]?.checklist_version_id ||
    !data.runs[0]?.checklist_version?.checklistByVersionChecklistId
  ) {
    router.push('/assigned');
    return <></>;
  }

  if (showAssignorMessage) {
    return (
      <>
        <div className="fixed inset-x-0 bottom-0 flex justify-center p-4">
          <div
            className="bg-black/[0.5] rounded-full text-white px-4 py-3"
            role="alert"
          >
            <div className="flex">
              <div>
                <p className="text-sm">{`You (Assignor) cannot edit the Assigner's Checklist Run`}</p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Head>
        <title>{run?.name || 'Untitled'} | Checklist GG</title>
        <MetaInfo metaInfo={metaInfoConstants.DEFAULT_META_INFO}></MetaInfo>
      </Head>

      <RenderRunComponent
        runId={runId as string}
        run={run}
        wfMap={wfMap}
        statusMap={statusMap}
        isPreview={preview === 'true' ? true : false}
        isPublic={false}
        isSingleResponse={false}
      />
    </>
  );
}

/**
 * Run will be rendered from multiple places
 *
 * 1. Assigned run
 * 2. Public run
 * 3. Builder preview
 * 4. Single response view in Responses
 */
export function RenderRunComponent({
  runId,
  run,
  wfMap,
  statusMap,
  isPreview,
  isPublic,
  isSingleResponse,
  isBuilderPreview
}: {
  runId: string;
  run: Run;
  wfMap?: Record<string, WorkflowAssignment[]>;
  statusMap?: Record<string, RunsTaskAssignment[]>;
  isPreview: boolean;
  isPublic: boolean;
  isSingleResponse: boolean;
  isBuilderPreview?: boolean;
}) {
  // Variables
  const userId = useUserId();

  // States
  const [runChecklistTree, setRunChecklistTree] = useState<RunChecklistTree>();

  // Store
  const [
    setIsPreview,
    setIsPublic,
    setIsStatusIndicatorDisabled,
    setIsSingleResponse,
    setIsBuilderPreview,
    reset
  ] = useRunChecklistStore(
    s => [
      s.setIsPreview,
      s.setIsPublic,
      s.setIsStatusIndicatorDisabled,
      s.setIsSingleResponse,
      s.setIsBuilderPreview,
      s.reset
    ],
    shallow
  );

  // GraphQL
  const [insertRunChecklistMutation] = useMutation(
    INSERT_RUN_CHECKLIST_MUTATION
  );

  useEffect(() => {
    // Set preview mode
    setIsPreview(isPreview === true ? true : false);

    // Set public mode
    setIsPublic(isPublic === true ? true : false);

    setIsStatusIndicatorDisabled(isSingleResponse === true ? true : false);

    // Set builder preview mode
    setIsBuilderPreview(isBuilderPreview === true ? true : false);

    // Set single response view flag
    setIsSingleResponse(isSingleResponse === true ? true : false);

    if (run) {
      const runObject = JSON.parse(JSON.stringify(run));

      const runChecklistByTaskId: RunChecklistByTaskId = {};
      formRunChecklistByTaskId(runObject, runChecklistByTaskId);

      const newRunChecklists: RunChecklist[] = [];
      formRunChecklistTree(
        runObject,
        runChecklistByTaskId,
        newRunChecklists,
        wfMap ?? {},
        statusMap ?? {}
      );

      // Check if any item in newRunChecklists has an empty run_checklist_id
      const hasEmptyRunChecklistId = newRunChecklists?.some(
        item => !item.run_checklist_id // Check if run_checklist_id is empty
      );

      // Insert all missed runChecklist
      if (
        isPreview !== true &&
        isPublic !== true &&
        isSingleResponse !== true &&
        !hasEmptyRunChecklistId
      ) {
        insertNewRunChecklist(newRunChecklists);
      }
    }
  }, [run, isPreview, isPublic, wfMap, statusMap]);

  function formRunChecklistByTaskId(
    runObject: Run,
    runChecklistByTaskId: RunChecklistByTaskId
  ) {
    if (runObject?.run_checklists && runObject?.run_checklists?.length > 0) {
      runObject.run_checklists.forEach(runChecklist => {
        if (runChecklist.task_id) {
          runChecklistByTaskId[runChecklist.task_id] = runChecklist;
        }
      });
    }
  }

  function insertNewRunChecklist(newRunChecklists: RunChecklist[]) {
    if (newRunChecklists?.length > 0) {
      insertRunChecklistMutation({
        variables: {
          checklist: newRunChecklists
        }
      });
    }
  }

  function createRunChecklist(
    versionChecklist: RunChecklist,
    runChecklistByTaskId: RunChecklistByTaskId,
    runObject: Run,
    newRunChecklists: RunChecklist[]
  ) {
    const newRunChecklist: RunChecklist = {
      id: uuidv4(),
      task_id: versionChecklist?.task_id,
      run_id: runId as string,
      run_checklist_id: runObject?.run_checklist_id,
      assigned_by: runObject?.created_by
    };

    if (newRunChecklist?.task_id) {
      runChecklistByTaskId[newRunChecklist.task_id] = newRunChecklist;
    }

    newRunChecklists.push(newRunChecklist);
    return newRunChecklist;
  }

  function updateRunChecklistProps(
    versionChecklist: RunChecklist,
    runChecklistByTaskId: RunChecklistByTaskId,
    runObject: Run,
    newRunChecklists: RunChecklist[],
    wfMap: Record<string, WorkflowAssignment[]>,
    statusMap: Record<string, RunsTaskAssignment[]>
  ) {
    // Create new RunChecklist, if not found
    if (
      versionChecklist.task_id &&
      !runChecklistByTaskId[versionChecklist.task_id]
    ) {
      createRunChecklist(
        versionChecklist,
        runChecklistByTaskId,
        runObject,
        newRunChecklists
      );
    }

    // Take RunChecklist status
    if (versionChecklist.task_id) {
      versionChecklist['run_checklist_task_id'] =
        runChecklistByTaskId[versionChecklist.task_id].id;
      versionChecklist['run_checklist_id'] =
        runChecklistByTaskId[versionChecklist.task_id].run_checklist_id;
      versionChecklist['is_completed'] =
        runChecklistByTaskId[versionChecklist.task_id].is_completed;
      versionChecklist['run_id'] =
        runChecklistByTaskId[versionChecklist.task_id].run_id;
      versionChecklist['checklist_status_indicators_id'] =
        runChecklistByTaskId[
          versionChecklist.task_id
        ].checklist_status_indicators_id;
      if (wfMap && statusMap && versionChecklist?.id) {
        versionChecklist.assignedUsers = wfMap[versionChecklist?.id] || [];
        versionChecklist.assignedUserStatus =
          statusMap[versionChecklist.id!] || [];
      }
      if (
        runObject?.runs_assignments &&
        runObject?.runs_assignments?.length > 0
      ) {
        versionChecklist.runs_assignments = runObject?.runs_assignments || [];
      }
      if (runObject?.workflow_configuration) {
        versionChecklist.workflow_configuration =
          runObject?.workflow_configuration || [];
      }
    }
  }

  function formChildRunChecklistTree(
    node: RunChecklistTree,
    allChildRunChecklist: RunChecklist[],
    rootNode: RunChecklistTree,
    runChecklistByTaskId: RunChecklistByTaskId,
    runObject: Run,
    newRunChecklists: RunChecklist[],
    wfMap: Record<string, WorkflowAssignment[]>,
    statusMap: Record<string, RunsTaskAssignment[]>
  ) {
    // Capture last child node
    rootNode.lastChildNode = node;

    const myChildrenRunChecklist = allChildRunChecklist.filter(
      checklist => checklist.parent_id === node.checklist.id
    );

    myChildrenRunChecklist.sort((a, b) => {
      return (a.order_number as number) - (b.order_number as number);
    });

    node.children = myChildrenRunChecklist.map((checklist, index) => {
      updateRunChecklistProps(
        checklist,
        runChecklistByTaskId,
        runObject,
        newRunChecklists,
        wfMap,
        statusMap
      );

      return {
        checklist: checklist,
        children: [],
        parent: node,
        isFirstNode: index === 0,
        isLastNode: index === myChildrenRunChecklist.length - 1
      };
    });

    node.children.forEach(childNode => {
      formChildRunChecklistTree(
        childNode,
        allChildRunChecklist,
        rootNode,
        runChecklistByTaskId,
        runObject,
        newRunChecklists,
        wfMap,
        statusMap
      );
    });
  }

  function formRunChecklistTree(
    runObject: Run,
    runChecklistByTaskId: RunChecklistByTaskId,
    newRunChecklists: RunChecklist[],
    wfMap: Record<string, WorkflowAssignment[]>,
    statusMap: Record<string, RunsTaskAssignment[]>
  ) {
    if (runObject?.checklist_version?.checklistByVersionChecklistId) {
      const versionChecklistObject =
        runObject.checklist_version.checklistByVersionChecklistId;

      const root = runObject?.checklist_version?.checklistByVersionChecklistId;
      // set on root
      if (wfMap && statusMap && root.task_id) {
        versionChecklistObject.assignedUsers = wfMap[root.task_id] || [];
        versionChecklistObject.assignedUserStatus =
          statusMap[root.task_id] || [];
      }

      if (
        runObject?.runs_assignments &&
        runObject?.runs_assignments?.length > 0
      ) {
        versionChecklistObject.runs_assignments =
          runObject?.runs_assignments || [];
      }
      if (runObject?.workflow_configuration) {
        versionChecklistObject.workflow_configuration =
          runObject?.workflow_configuration || [];
      }

      updateRunChecklistProps(
        versionChecklistObject,
        runChecklistByTaskId,
        runObject,
        newRunChecklists,
        wfMap,
        statusMap
      );

      const _rootNode = {
        checklist: versionChecklistObject,
        children: [],
        parent: undefined
      } as RunChecklistTree;

      formChildRunChecklistTree(
        _rootNode,
        versionChecklistObject?.checklistsByChecklistId as RunChecklist[],
        _rootNode,
        runChecklistByTaskId,
        runObject,
        newRunChecklists,
        wfMap,
        statusMap
      );

      setRunChecklistTree(_rootNode);
    }
  }

  function isRunChecklistCompletedForUser(
    run: Run,
    wfMap: Record<string, WorkflowAssignment[]> = {}
  ): boolean {
    if (!run?.run_checklists || !run.run_checklist_id) {
      return false;
    }

    // 1) find the root checklist item
    const matched = run.run_checklists.find(
      item => item.id === run.run_checklist_id
    );
    if (!matched || !matched.task_id) return false;

    // 2) look up who’s assigned to that task
    const assignedList = wfMap[matched.task_id] || [];

    // 3a) if nobody's explicitly assigned → global completion
    if (assignedList.length === 0) {
      return matched.is_completed === true;
    }

    // 3b) otherwise, only you count:
    const meAssigned = assignedList.some(a => a.user_id === userId);
    return meAssigned && matched.is_completed === true;
  }

  return (
    <>
      {/* Body */}
      {runChecklistTree && (
        <RunBuilderComponent
          runCompleted={isRunChecklistCompletedForUser(run, wfMap)}
          runObject={run}
          runChecklistTree={runChecklistTree}
        />
      )}
    </>
  );
}

function GetRunChecklistInstructionsMappingComponent({
  runId
}: {
  runId: string;
}) {
  // Variables

  // Store
  const [setRunChecklistInstructionsMappingObject] = useRunChecklistStore(
    s => [s.setRunChecklistInstructionsMappingObject],
    shallow
  );

  // GraphQL
  const { data, loading, error } = useQuery<{ runs: Run[] }>(
    GET_RUN_CHECKLIST_INSTRUCTION_BY_RUN_ID_QUERY,
    {
      variables: { id: runId },
      fetchPolicy: 'network-only'
    }
  );

  useEffect(() => {
    if (data?.runs[0]) {
      const runObject = data?.runs[0];
      const componentMappingObject: { [key: string]: boolean } = {};

      if (runObject?.checklist_version?.checklistByVersionChecklistId) {
        if (
          runObject?.checklist_version?.checklistByVersionChecklistId?.id &&
          runObject?.checklist_version?.checklistByVersionChecklistId
            ?.checklist_component_mappings &&
          runObject?.checklist_version?.checklistByVersionChecklistId
            ?.checklist_component_mappings?.length > 0
        ) {
          componentMappingObject[
            runObject.checklist_version.checklistByVersionChecklistId.id
          ] = true;
        }

        // Check for all children
        if (
          runObject?.checklist_version?.checklistByVersionChecklistId
            ?.checklistsByChecklistId &&
          runObject?.checklist_version?.checklistByVersionChecklistId
            ?.checklistsByChecklistId?.length > 0
        ) {
          const tasks =
            runObject?.checklist_version?.checklistByVersionChecklistId
              ?.checklistsByChecklistId;

          tasks.forEach(task => {
            if (
              task?.id &&
              task?.checklist_component_mappings &&
              task?.checklist_component_mappings?.length > 0
            ) {
              componentMappingObject[task.id] = true;
            }
          });
        }
      }

      setRunChecklistInstructionsMappingObject(componentMappingObject);
    }
  }, [data]);

  return <></>;
}

function GetPublicRunChecklistInstructionsMappingComponent({
  runId
}: {
  runId: string;
}) {
  // Variables

  // Store
  const [setRunChecklistInstructionsMappingObject] = useRunChecklistStore(
    s => [s.setRunChecklistInstructionsMappingObject],
    shallow
  );

  useEffect(() => {
    const { token: cancelToken, cancel } = axios.CancelToken.source();

    // Get checklist instruction mappings
    fetchChecklistInstructionMappings(runId, cancelToken);

    return () => {
      cancel('Cancelled');
    };
  }, []);

  async function fetchChecklistInstructionMappings(
    runId: string,
    cancelToken: CancelToken
  ) {
    // Fetch all instruction mappings
    try {
      const payload = {
        runId: runId
      };

      const response = await axios.post(
        CommonConstants.API_PREFIX +
          '/public/run/get-checklist-instruction-mappings',
        payload,
        {
          cancelToken: cancelToken
        }
      );

      if (
        response?.data?.data?.public_runs &&
        response?.data?.data?.public_runs?.length > 0
      ) {
        const result = response?.data?.data?.public_runs[0];

        updateChecklistInstructionMappingsToStore(result);
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error?.response?.data?.error?.message || error.message);
      } else {
        console.error('Something Went wrong. Please try again');
      }
    }
  }

  function updateChecklistInstructionMappingsToStore(runObject: Run) {
    if (runObject) {
      const componentMappingObject: { [key: string]: boolean } = {};

      if (runObject?.checklist_version?.checklistByVersionChecklistId) {
        if (
          runObject?.checklist_version?.checklistByVersionChecklistId?.id &&
          runObject?.checklist_version?.checklistByVersionChecklistId
            ?.checklist_component_mappings &&
          runObject?.checklist_version?.checklistByVersionChecklistId
            ?.checklist_component_mappings?.length > 0
        ) {
          componentMappingObject[
            runObject.checklist_version.checklistByVersionChecklistId.id
          ] = true;
        }

        // Check for all children
        if (
          runObject?.checklist_version?.checklistByVersionChecklistId
            ?.checklistsByChecklistId &&
          runObject?.checklist_version?.checklistByVersionChecklistId
            ?.checklistsByChecklistId?.length > 0
        ) {
          const tasks =
            runObject?.checklist_version?.checklistByVersionChecklistId
              ?.checklistsByChecklistId;

          tasks.forEach(task => {
            if (
              task?.id &&
              task?.checklist_component_mappings &&
              task?.checklist_component_mappings?.length > 0
            ) {
              componentMappingObject[task.id] = true;
            }
          });
        }
      }

      setRunChecklistInstructionsMappingObject(componentMappingObject);
    }
  }

  return <></>;
}
